import React, { useState, useEffect, useRef } from 'react';
import { Plus, Save, Trash2, Upload, X, GripVertical, Eye, EyeOff, Pencil, ImageIcon } from 'lucide-react';
import { api } from '../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function VehicleCategoryCMS() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ vehicle_id: '', name: '', base_price: 30, passengers: 4, luggage: 3, sort_order: 99 });
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/admin/vehicle-categories');
      setCategories(res.data);
    } catch (e) {
      console.error('Failed to load categories', e);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditForm({
      name: cat.name,
      name_de: cat.name_de || cat.name,
      name_en: cat.name_en || cat.name,
      base_price: cat.base_price,
      passengers: cat.passengers,
      luggage: cat.luggage,
      sort_order: cat.sort_order,
      active: cat.active,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (catId) => {
    setSaving(true);
    try {
      await api.put(`/admin/vehicle-categories/${catId}`, editForm);
      setEditingId(null);
      fetchCategories();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!addForm.vehicle_id || !addForm.name) {
      alert('Bitte ID und Name ausfüllen');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/vehicle-categories', addForm);
      setShowAddForm(false);
      setAddForm({ vehicle_id: '', name: '', base_price: 30, passengers: 4, luggage: 3, sort_order: 99 });
      fetchCategories();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (catId, catName) => {
    if (!window.confirm(`Kategorie "${catName}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/vehicle-categories/${catId}`);
      fetchCategories();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleImageUpload = async (catId, file) => {
    setUploadingId(catId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/admin/vehicle-categories/${catId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchCategories();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingId(null);
    }
  };

  const triggerFileInput = (catId) => {
    setUploadingId(catId);
    fileInputRef.current?.click();
  };

  const onFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (file && uploadingId) {
      handleImageUpload(uploadingId, file);
    }
    e.target.value = '';
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-slate-400">Laden...</div>;
  }

  return (
    <div className="space-y-4" data-testid="vehicle-cms">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={onFileSelected}
      />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Fahrzeugkategorien</h2>
          <p className="text-xs text-slate-400 mt-0.5">{categories.length} Kategorien verwalten</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600 transition-colors"
          data-testid="add-category-btn"
        >
          <Plus size={14} /> Neue Kategorie
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-3" data-testid="add-category-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Neue Kategorie</h3>
            <button onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">ID (z.B. luxury)</label>
              <input
                value={addForm.vehicle_id}
                onChange={e => setAddForm(p => ({ ...p, vehicle_id: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                placeholder="luxury"
                data-testid="add-vehicle-id"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Name</label>
              <input
                value={addForm.name}
                onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                placeholder="Luxury"
                data-testid="add-vehicle-name"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Grundpreis (€)</label>
              <input
                type="number"
                value={addForm.base_price}
                onChange={e => setAddForm(p => ({ ...p, base_price: Number(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                data-testid="add-vehicle-price"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Passagiere</label>
              <input
                type="number"
                value={addForm.passengers}
                onChange={e => setAddForm(p => ({ ...p, passengers: Number(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                data-testid="add-vehicle-passengers"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Gepäck</label>
              <input
                type="number"
                value={addForm.luggage}
                onChange={e => setAddForm(p => ({ ...p, luggage: Number(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                data-testid="add-vehicle-luggage"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Reihenfolge</label>
              <input
                type="number"
                value={addForm.sort_order}
                onChange={e => setAddForm(p => ({ ...p, sort_order: Number(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                data-testid="add-vehicle-sort"
              />
            </div>
          </div>
          <button
            onClick={handleAdd}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600 disabled:opacity-50"
            data-testid="save-new-category-btn"
          >
            <Save size={14} /> {saving ? 'Speichern...' : 'Kategorie erstellen'}
          </button>
        </div>
      )}

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {categories.map(cat => (
          <div
            key={cat.id}
            className={`bg-white rounded-xl border ${cat.active ? 'border-slate-200' : 'border-red-200 bg-red-50/30'} overflow-hidden`}
            data-testid={`category-card-${cat.vehicle_id}`}
          >
            {/* Image Area */}
            <div className="relative h-32 bg-slate-50 flex items-center justify-center border-b border-slate-100">
              {cat.image_path ? (
                <img
                  src={`${API_URL}/api/vehicle-categories/${cat.id}/image`}
                  alt={cat.name}
                  className="h-full w-full object-contain p-3"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="flex flex-col items-center text-slate-300">
                  <ImageIcon size={32} />
                  <span className="text-xs mt-1">Kein Bild</span>
                </div>
              )}
              <button
                onClick={() => triggerFileInput(cat.id)}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-orange-50 hover:text-orange-500 hover:border-orange-300 transition-colors"
                title="Bild hochladen"
                data-testid={`upload-image-${cat.vehicle_id}`}
              >
                {uploadingId === cat.id ? (
                  <div className="w-3.5 h-3.5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Upload size={13} />
                )}
              </button>
              {!cat.active && (
                <span className="absolute top-2 left-2 px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-medium rounded-full">
                  Deaktiviert
                </span>
              )}
            </div>

            {/* Details */}
            {editingId === cat.id ? (
              <div className="p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-slate-400">Name</label>
                    <input
                      value={editForm.name || ''}
                      onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm"
                      data-testid={`edit-name-${cat.vehicle_id}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400">Grundpreis (€)</label>
                    <input
                      type="number"
                      value={editForm.base_price ?? ''}
                      onChange={e => setEditForm(p => ({ ...p, base_price: Number(e.target.value) }))}
                      className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm"
                      data-testid={`edit-price-${cat.vehicle_id}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400">Passagiere</label>
                    <input
                      type="number"
                      value={editForm.passengers ?? ''}
                      onChange={e => setEditForm(p => ({ ...p, passengers: Number(e.target.value) }))}
                      className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm"
                      data-testid={`edit-passengers-${cat.vehicle_id}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400">Gepäck</label>
                    <input
                      type="number"
                      value={editForm.luggage ?? ''}
                      onChange={e => setEditForm(p => ({ ...p, luggage: Number(e.target.value) }))}
                      className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm"
                      data-testid={`edit-luggage-${cat.vehicle_id}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400">Reihenfolge</label>
                    <input
                      type="number"
                      value={editForm.sort_order ?? ''}
                      onChange={e => setEditForm(p => ({ ...p, sort_order: Number(e.target.value) }))}
                      className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm"
                      data-testid={`edit-sort-${cat.vehicle_id}`}
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.active ?? true}
                        onChange={e => setEditForm(p => ({ ...p, active: e.target.checked }))}
                        className="rounded border-slate-300"
                      />
                      Aktiv
                    </label>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => saveEdit(cat.id)}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-medium hover:bg-emerald-600 disabled:opacity-50"
                    data-testid={`save-edit-${cat.vehicle_id}`}
                  >
                    <Save size={12} /> Speichern
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-semibold text-slate-800 text-sm">{cat.name}</h3>
                  <span className="text-xs font-mono text-slate-400">{cat.vehicle_id}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                  <span>ab €{cat.base_price}</span>
                  <span>{cat.passengers} Pers.</span>
                  <span>{cat.luggage} Gep.</span>
                  <span className="text-slate-300">#{cat.sort_order}</span>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => startEdit(cat)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs hover:bg-slate-200 transition-colors"
                    data-testid={`edit-category-${cat.vehicle_id}`}
                  >
                    <Pencil size={12} /> Bearbeiten
                  </button>
                  <button
                    onClick={() => handleDelete(cat.id, cat.name)}
                    className="px-2 py-1.5 bg-red-50 text-red-500 rounded-lg text-xs hover:bg-red-100 transition-colors"
                    data-testid={`delete-category-${cat.vehicle_id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="text-center py-12 text-slate-400 text-sm">
          Keine Kategorien vorhanden. Erstellen Sie eine neue Kategorie.
        </div>
      )}
    </div>
  );
}
